import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Check, X, Shield, Fingerprint as FingerIcon, AlertTriangle } from "lucide-react";

export default function FraudQueue() {
  const [tab, setTab] = useState("queue");
  const [items, setItems] = useState([]);
  const [fps, setFps] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [q, f] = await Promise.all([
      api.get("/fraud/queue"),
      api.get("/fraud/fingerprints"),
    ]);
    setItems(q.data);
    setFps(f.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const review = async (id, decision) => {
    const note = decision === "reject" ? window.prompt("Reason (optional)?") || "" : "";
    await api.post(`/fraud/${id}/review`, { decision, note });
    load();
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight text-[#1a1c19] sm:text-3xl flex items-center gap-2">
          <Shield className="h-6 w-6 text-[#ff3399]" /> Fraud Signals
        </h1>
        <p className="text-sm text-[#5c665a]">Review flagged referrals and monitor device-level risk.</p>
      </div>

      <div className="flex items-center gap-2 border-b border-[#e2e8e2]">
        <button
          className={`px-4 py-2 text-sm font-semibold ${tab === "queue" ? "border-b-2 border-[#66cc33] text-[#66cc33]" : "text-[#5c665a]"}`}
          onClick={() => setTab("queue")}
          data-testid="fraud-tab-queue"
        >
          Queue ({items.length})
        </button>
        <button
          className={`px-4 py-2 text-sm font-semibold ${tab === "fp" ? "border-b-2 border-[#66cc33] text-[#66cc33]" : "text-[#5c665a]"}`}
          onClick={() => setTab("fp")}
          data-testid="fraud-tab-fp"
        >
          <FingerIcon className="inline h-4 w-4 mr-1" /> Device fingerprints ({fps.length})
        </button>
      </div>

      {tab === "queue" ? (
        <div className="admin-card overflow-x-auto p-0">
          <table className="min-w-full text-sm">
            <thead className="bg-[#fafcfa] text-left text-xs uppercase tracking-wider text-[#5c665a]">
              <tr>
                <th className="px-4 py-3">Referrer</th>
                <th className="px-4 py-3">Friend</th>
                <th className="px-4 py-3">Flags</th>
                <th className="px-4 py-3">Device</th>
                <th className="px-4 py-3">Reward</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e2e8e2]" data-testid="fraud-table">
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-[#5c665a]">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-[#5c665a]">No flagged referrals. 🎉</td></tr>
              ) : items.map((r) => (
                <tr key={r.id}>
                  <td className="px-4 py-3">
                    <div className="font-semibold">{r.referrer_name}</div>
                    <div className="text-xs text-[#5c665a]">{r.referrer_phone}</div>
                  </td>
                  <td className="px-4 py-3 text-xs">{r.friend_phone}</td>
                  <td className="px-4 py-3">
                    <div className="space-y-1">
                      {(r.fraud_flags || []).map((f) => (
                        <span key={f} className="badge-pink mr-1">{f}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[10px] font-mono text-[#5c665a]">
                    {r.device_signals?.device_fp ? r.device_signals.device_fp.slice(0, 10) + "…" : "—"}
                    {r.device_signals?.ip && <div>IP: {r.device_signals.ip}</div>}
                  </td>
                  <td className="px-4 py-3 font-semibold">{r.reward_amount ? `KES ${r.reward_amount}` : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button className="admin-btn mr-2" onClick={() => review(r.id, "approve")} data-testid={`approve-${r.id}`}>
                      <Check className="h-3.5 w-3.5" /> Approve
                    </button>
                    <button className="admin-btn-danger" onClick={() => review(r.id, "reject")} data-testid={`reject-${r.id}`}>
                      <X className="h-3.5 w-3.5" /> Reject
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="admin-card overflow-x-auto p-0" data-testid="fp-card">
          <div className="flex items-start gap-3 border-b border-[#e2e8e2] bg-[#fff0f5] px-4 py-3 text-xs text-[#c01974]">
            <AlertTriangle className="mt-0.5 h-4 w-4" />
            <div>
              Fingerprints shared across 2+ different phones indicate <strong>shared-device or SIM-swap abuse</strong>.
              Captures from such devices are auto-flagged with <code>device_fp_shared</code> or <code>sim_swap_suspected</code>.
            </div>
          </div>
          <table className="min-w-full text-sm">
            <thead className="bg-[#fafcfa] text-left text-xs uppercase tracking-wider text-[#5c665a]">
              <tr>
                <th className="px-4 py-3">Fingerprint</th>
                <th className="px-4 py-3">Phones</th>
                <th className="px-4 py-3">Uses</th>
                <th className="px-4 py-3">Last IP</th>
                <th className="px-4 py-3">Last UA</th>
                <th className="px-4 py-3">Last seen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e2e8e2]">
              {fps.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-[#5c665a]">No device fingerprints yet.</td></tr>
              ) : fps.map((f) => (
                <tr key={f.fp} className={f.phone_count >= 2 ? "bg-[#fff0f5]" : ""}>
                  <td className="px-4 py-3 font-mono text-xs">{f.fp.slice(0, 14)}…</td>
                  <td className="px-4 py-3">
                    <span className={f.phone_count >= 2 ? "badge-pink" : "badge-green"}>{f.phone_count}</span>
                    <div className="mt-1 text-[10px] text-[#5c665a]">{(f.phones || []).join(", ")}</div>
                  </td>
                  <td className="px-4 py-3">{f.uses}</td>
                  <td className="px-4 py-3 font-mono text-xs">{f.last_ip || "—"}</td>
                  <td className="px-4 py-3 max-w-xs truncate text-xs text-[#5c665a]" title={f.last_ua}>{f.last_ua || "—"}</td>
                  <td className="px-4 py-3 text-xs text-[#5c665a]">{f.last_seen?.slice(0, 19).replace("T", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
